import {Component, Input, OnInit, TrackByFunction} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MobileOperator, MobileOperatorEventSource, MsisdnFraudEntryRequest, MsisdnStatus, SubscriberCategory} from '@npck/af-frontend-api';
import {DATE_SERVER_FORMAT, getFormattedDateOrNull} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {filter} from 'rxjs';
import {DateForm} from '../../../../components/date-picker-field/date-picker-field.component';
import {getValidPhoneNumberOrNull} from '../../../../domain/phone-utils';
import {sanitizeDigits} from '../create-edit-incident.component';
import {MsisdnFraudEntryForm} from './msisdn-fraud-form/msisdn-fraud-form.component';

@Component({
    selector: 'af-msisdn-fraud-step',
    templateUrl: './msisdn-fraud-step.component.html',
    styleUrl: './msisdn-fraud-step.component.scss',
    standalone: false,
})
export class MsisdnFraudStepComponent implements OnInit {
    @Input() form: FormGroup<MsisdnFraudEntriesForm>;

    MAX_ENTRIES = 50;

    constructor(
        private formBuilder: FormBuilder,
        private dialogService: DialogService,
    ) {
    }

    get showDeleteButton(): boolean {
        return (this.form?.controls?.entries?.controls?.length ?? 0) > 1;
    }

    get canAddMoreEntries(): boolean {
        return (this.form?.controls?.entries?.controls?.length ?? 0) < this.MAX_ENTRIES;
    }

    ngOnInit(): void {
        this.form.addControl('entries', this.formBuilder.array([] as FormGroup<MsisdnFraudEntryForm>[]));
        this.addEntry();
    }

    trackByEntryIndex: TrackByFunction<FormGroup<MsisdnFraudEntryForm>> = (index: number) => index;

    addEntry(): void {
        const entryForm = this.createEntryForm();
        this.form.controls.entries.push(entryForm);
    }

    deleteEntry(index: number): void {
        this.dialogService.openDialog({
            title: 'i18n_af_msisdnFraudEntryDeletionDialogTitle',
            subtitle: 'i18n_af_msisdnFraudEntryDeletionDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {index: `${index + 1}`},
        })
            .pipe(filter(Boolean))
            .subscribe(() => this.form.controls.entries.removeAt(index));
    }

    private createEntryForm(): FormGroup<MsisdnFraudEntryForm> {
        return this.formBuilder.group<MsisdnFraudEntryForm>({
            msisdn: new FormControl(null, [Validators.required, Validators.maxLength(15)]),
            selectedPhoneInfo: new FormControl(null),
            operator: new FormControl(null, [Validators.required]),
            activationDate: this.formBuilder.group<DateForm>({
                date: new FormControl(null),
            }),
            terminationDate: this.formBuilder.group<DateForm>({
                date: new FormControl(null),
            }),
            status: new FormControl(null, [Validators.required]),
            reason: new FormControl(MobileOperatorEventSource.OPERATOR_FRAUD_SYSTEM),
        });
    }
}

export interface MsisdnFraudEntriesForm {
    entries?: FormArray<FormGroup<MsisdnFraudEntryForm>>;
}

export function createMsisdnFraudEntryRequest(entryForm: FormGroup<MsisdnFraudEntryForm>): MsisdnFraudEntryRequest {
    const v = entryForm.getRawValue();
    const phoneNumber = getValidPhoneNumberOrNull(v.msisdn, v.selectedPhoneInfo)?.replace('+', '') ?? sanitizeDigits(v.msisdn);
    const subscriberMsisdns = v.subscriberMsisdns
        ?.map(s => getValidPhoneNumberOrNull(s.msisdn, s.selectedPhoneInfo)?.replace('+', '') ?? sanitizeDigits(s.msisdn))
        .filter(Boolean);
    return {
        msisdn: phoneNumber,
        operator: v.operator as MobileOperator,
        status: v.status as MsisdnStatus,
        afBlocked: null,
        reason: (v.reason as MobileOperatorEventSource) ?? null,
        contactDate: getFormattedDateOrNull(v.contactDate?.date),
        category: (v.category as SubscriberCategory) ?? SubscriberCategory.SUSPECTED,
        isResident: v.isResident,
        iin: v.iin ?? null,
        passportNumber: v.passportNumber ?? null,
        lastName: v.lastName,
        firstName: v.firstName,
        middleName: v.middleName ?? null,
        country: v.country ?? null,
        subscriberMsisdns: subscriberMsisdns?.length ? subscriberMsisdns : null,
        activationDate: getFormattedDateOrNull(v.activationDate?.date, DATE_SERVER_FORMAT),
        terminationDate: getFormattedDateOrNull(v.terminationDate?.date, DATE_SERVER_FORMAT),
    };
}
